import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiMail, FiClock, FiPlus, FiTrash2, FiSend, FiCheckCircle, FiX, FiPieChart, FiFilter, FiBell, FiHardDrive, FiSettings, FiAlertCircle } = FiIcons;

const ReportScheduler = () => {
  const [schedules, setSchedules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [newReport, setNewReport] = useState({
    name: '',
    frequency: 'Weekly',
    metrics: ['Traffic Overview'],
    recipients: '',
    status: 'Active',
    notification_prefs: {
      email_on_success: true,
      email_on_failure: true,
      include_csv: false
    }
  });

  const availableMetrics = [
    { id: 'Traffic Overview', icon: FiPieChart },
    { id: 'User Identification', icon: FiIcons.FiUser },
    { id: 'Funnel Performance', icon: FiFilter },
    { id: 'Custom Events', icon: FiIcons.FiZap },
    { id: 'Device Breakdown', icon: FiIcons.FiMonitor }
  ];

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    const { data } = await supabase.from('report_schedules_1739812345680').select('*').order('created_at', { ascending: false });
    if (data) setSchedules(data);
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    const recipientsArray = newReport.recipients.split(',').map(email => email.trim());
    
    const { error } = await supabase
      .from('report_schedules_1739812345680')
      .insert([{ ...newReport, recipients: recipientsArray, notification_prefs: newReport.notification_prefs }]);

    if (!error) {
      await fetchSchedules();
      setShowModal(false);
      setNewReport({ 
        name: '', frequency: 'Weekly', metrics: ['Traffic Overview'], recipients: '', status: 'Active', 
        notification_prefs: { email_on_success: true, email_on_failure: true, include_csv: false }
      });
    }
    setLoading(false);
  };

  const toggleNotification = (key) => {
    setNewReport(prev => ({
      ...prev,
      notification_prefs: {
        ...prev.notification_prefs,
        [key]: !prev.notification_prefs[key]
      }
    }));
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-2xl shadow-blue-900/5 relative overflow-hidden gap-6">
        <div className="relative z-10">
          <h2 className="text-3xl font-black text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-blue-200">
               <SafeIcon icon={FiMail} />
            </div>
            Reporting Intelligence
          </h2>
          <p className="text-sm text-gray-500 font-bold mt-2 max-w-lg uppercase tracking-wider">Automate high-fidelity intelligence delivery to your organization's decision makers.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-10 py-4 rounded-2xl flex items-center gap-3 hover:bg-blue-700 transition-all shadow-2xl shadow-blue-200 font-black uppercase tracking-widest text-xs relative z-10"
        >
          <SafeIcon icon={FiPlus} /> Create Schedule
        </button>
        <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none">
           <SafeIcon icon={FiMail} className="w-64 h-64" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {schedules.map((schedule) => (
          <motion.div 
            key={schedule.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="group bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden flex flex-col hover:shadow-2xl hover:border-blue-100 transition-all"
          >
            <div className="p-8 border-b border-gray-50 bg-gray-50/50 flex justify-between items-start">
              <div>
                <h3 className="font-black text-gray-900 text-xl tracking-tight">{schedule.name}</h3>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-blue-600 font-black uppercase tracking-[0.2em]">
                  <SafeIcon icon={FiClock} /> {schedule.frequency} Cadence
                </div>
              </div>
              <button 
                onClick={async () => {
                  await supabase.from('report_schedules_1739812345680').delete().eq('id', schedule.id);
                  fetchSchedules();
                }}
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all border border-transparent hover:border-red-100"
              >
                <SafeIcon icon={FiTrash2} className="text-xl" />
              </button>
            </div>
            
            <div className="p-8 flex-1 space-y-8">
              <div>
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Intelligence Scope</div>
                <div className="flex flex-wrap gap-2">
                  {schedule.metrics.map(m => (
                    <span key={m} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-blue-100/50">
                      {m}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Recipient Matrix</div>
                <div className="space-y-3">
                  {schedule.recipients.map(email => (
                    <div key={email} className="text-xs font-bold text-gray-700 flex items-center gap-3 bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                      {email}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-gray-50">
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                   <SafeIcon icon={FiBell} /> Notifications
                </div>
                <div className="flex gap-4">
                   <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${schedule.notification_prefs?.email_on_success ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-300'}`}>
                      <SafeIcon icon={FiCheckCircle} /> Success
                   </div>
                   <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${schedule.notification_prefs?.include_csv ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-50 text-gray-300'}`}>
                      <SafeIcon icon={FiHardDrive} /> Data Port
                   </div>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest leading-none mb-1">Last Transmission</span>
                <span className="text-xs font-bold text-gray-900">
                  {schedule.last_sent ? format(new Date(schedule.last_sent), 'MMM dd, HH:mm') : 'Pending Initial Run'}
                </span>
              </div>
              <button className="bg-white text-blue-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-blue-600 hover:text-white px-5 py-2.5 rounded-xl transition-all border border-blue-200 shadow-sm hover:shadow-lg">
                <SafeIcon icon={FiSend} /> Manual Trigger
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-xl flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              className="bg-white rounded-[3rem] shadow-2xl max-w-3xl w-full overflow-hidden border border-white/20"
            >
              <div className="p-10 border-b flex justify-between items-center bg-gray-50/50">
                <div>
                  <h3 className="text-3xl font-black text-gray-900 tracking-tight">Deploy Intelligence</h3>
                  <p className="text-sm text-gray-500 font-bold uppercase tracking-widest mt-1">Configure automated reporting parameters</p>
                </div>
                <button onClick={() => setShowModal(false)} className="w-14 h-14 rounded-full bg-white shadow-xl flex items-center justify-center text-gray-400 hover:text-gray-900 transition-all border border-gray-100 hover:scale-110">
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>

              <form onSubmit={handleCreateReport} className="p-10 space-y-10 max-h-[75vh] overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Report Designation</label>
                    <input required className="w-full px-6 py-4 border-2 border-gray-100 rounded-2xl focus:border-blue-500 focus:ring-0 outline-none transition-all font-bold text-gray-900 placeholder:text-gray-300" placeholder="e.g. Monthly Retention Matrix" value={newReport.name} onChange={e => setNewReport({...newReport, name: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Transmission Cadence</label>
                    <select className="w-full px-6 py-4 border-2 border-gray-100 rounded-2xl bg-white outline-none font-bold text-gray-900 appearance-none" value={newReport.frequency} onChange={e => setNewReport({...newReport, frequency: e.target.value})}>
                      <option>Daily</option>
                      <option>Weekly</option>
                      <option>Monthly</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Recipient Identity Matrix (CSV)</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input required className="w-full pl-14 pr-6 py-4 border-2 border-gray-100 rounded-2xl focus:border-blue-500 outline-none font-bold text-sm" placeholder="analyst@corp.com, leadership@corp.com" value={newReport.recipients} onChange={e => setNewReport({...newReport, recipients: e.target.value})} />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Intelligence Aggregation Modules</label>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableMetrics.map(metric => (
                      <button
                        key={metric.id}
                        type="button"
                        onClick={() => {
                           const metrics = newReport.metrics.includes(metric.id) 
                             ? newReport.metrics.filter(m => m !== metric.id)
                             : [...newReport.metrics, metric.id];
                           setNewReport({...newReport, metrics});
                        }}
                        className={`flex flex-col items-center justify-center gap-4 p-6 rounded-3xl border-2 transition-all duration-300 group ${
                          newReport.metrics.includes(metric.id)
                            ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-xl shadow-blue-900/5'
                            : 'border-gray-100 text-gray-400 hover:border-blue-200'
                        }`}
                      >
                        <SafeIcon icon={metric.icon} className={`text-2xl transition-transform group-hover:scale-110 ${newReport.metrics.includes(metric.id) ? 'text-blue-600' : ''}`} />
                        <span className="text-[10px] font-black uppercase tracking-widest text-center leading-tight">{metric.id}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-900 p-8 rounded-[2rem] text-white">
                  <div className="flex items-center gap-3 mb-6">
                     <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                        <SafeIcon icon={FiSettings} className="text-blue-400" />
                     </div>
                     <h4 className="text-xs font-black uppercase tracking-[0.2em]">Communication Protocols</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <button 
                      type="button" 
                      onClick={() => toggleNotification('email_on_success')}
                      className={`flex items-center justify-between p-4 rounded-2xl transition-all border-2 ${newReport.notification_prefs.email_on_success ? 'border-blue-500 bg-blue-500/10' : 'border-white/5 bg-white/5'}`}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiCheckCircle} className={newReport.notification_prefs.email_on_success ? 'text-blue-400' : 'text-gray-500'} />
                        <span className="text-xs font-bold">Success Alerts</span>
                      </div>
                      <div className={`w-8 h-4 rounded-full relative transition-all ${newReport.notification_prefs.email_on_success ? 'bg-blue-500' : 'bg-gray-600'}`}>
                        <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${newReport.notification_prefs.email_on_success ? 'left-4.5' : 'left-0.5'}`}></div>
                      </div>
                    </button>

                    <button 
                      type="button" 
                      onClick={() => toggleNotification('include_csv')}
                      className={`flex items-center justify-between p-4 rounded-2xl transition-all border-2 ${newReport.notification_prefs.include_csv ? 'border-blue-500 bg-blue-500/10' : 'border-white/5 bg-white/5'}`}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiHardDrive} className={newReport.notification_prefs.include_csv ? 'text-blue-400' : 'text-gray-500'} />
                        <span className="text-xs font-bold">Raw Data Export</span>
                      </div>
                      <div className={`w-8 h-4 rounded-full relative transition-all ${newReport.notification_prefs.include_csv ? 'bg-blue-500' : 'bg-gray-600'}`}>
                        <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${newReport.notification_prefs.include_csv ? 'left-4.5' : 'left-0.5'}`}></div>
                      </div>
                    </button>
                    
                    <div className="col-span-full flex items-center gap-3 bg-white/5 p-4 rounded-2xl border border-white/5">
                      <SafeIcon icon={FiAlertCircle} className="text-yellow-400 flex-shrink-0" />
                      <p className="text-[10px] text-gray-400 font-bold leading-relaxed">Notifications will be dispatched to the primary administrative email linked to this organization profile.</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
                  <button type="button" onClick={() => setShowModal(false)} className="px-10 py-4 text-gray-400 font-black uppercase tracking-widest text-[10px] hover:text-gray-900 transition-colors">Abort</button>
                  <button type="submit" disabled={loading} className="px-14 py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] hover:bg-blue-700 shadow-2xl shadow-blue-200 disabled:opacity-50 transition-all flex items-center gap-2">
                    {loading ? 'Processing...' : 'Deploy Schedule'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReportScheduler;